/**
 * Client-safe hub sport registry (F4 sport picker, boards, awards and search). Order is the
 * picker order: Snow, Bike, Waves, Wind, Water. The three named paddle profiles (beta) show only
 * when they are public; otherwise the legacy `sup` board takes their place in Waves.
 */
import type {
  HubSport,
  HubSportGroup,
  HubSportGroupInfo,
  HubSportKey,
} from "./types";

/** Rows each board card shows. */
export const HUB_BOARD_ROWS_DESKTOP = 10;
export const HUB_BOARD_ROWS_PHONE = 5;

export const HUB_SPORT_GROUPS: readonly HubSportGroupInfo[] = Object.freeze([
  { key: "snow", label: "Snow" },
  { key: "bike", label: "Bike" },
  { key: "waves", label: "Waves" },
  { key: "wind", label: "Wind" },
  { key: "water", label: "Water" },
]);

/** Every registry entry, picker order. The legacy board sits where SUP surf sits. */
export const HUB_SPORTS: readonly HubSport[] = Object.freeze([
  {
    key: "piste", label: "Piste", lowerName: "piste", group: "snow", icon: "piste", unit: "resorts",
    trackerKey: "piste", boardPath: "/conditions/piste", spotLens: "piste", awardSport: "ski",
    awardIds: { headline: "ski-resort-of-the-year-piste", secondary: "ski-deepest-powder" },
    beta: false, seasonal: true, keywords: "ski skiing snowboard snowboarding piste resort",
  },
  {
    key: "off-piste", label: "Off-piste", lowerName: "off-piste", group: "snow", icon: "off-piste", unit: "resorts",
    trackerKey: "off-piste", boardPath: "/conditions/off-piste", spotLens: "off-piste", awardSport: "ski",
    awardIds: { headline: "ski-resort-of-the-year-offpiste", secondary: "ski-deepest-powder" },
    beta: false, seasonal: true, keywords: "off-piste offpiste freeride backcountry powder ski skiing",
  },
  {
    key: "touring", label: "Touring", lowerName: "ski touring", group: "snow", icon: "touring", unit: "resorts",
    trackerKey: "touring", boardPath: "/conditions/touring", spotLens: "touring", awardSport: "touring",
    awardIds: { headline: "touring-mecca", secondary: null },
    beta: false, seasonal: true, keywords: "ski touring skinning splitboard splitboarding skimo",
  },
  {
    key: "mtb", label: "MTB", lowerName: "mountain biking", group: "bike", icon: "mtb", unit: "resorts",
    trackerKey: "mtb", boardPath: "/conditions/mtb", spotLens: "mtb", awardSport: "mtb",
    awardIds: { headline: "mtb-trail-capital", secondary: "mtb-dust-bowl" },
    beta: false, seasonal: false, keywords: "mtb mountain bike biking bike park downhill enduro cycling",
  },
  {
    key: "surf", label: "Surf", lowerName: "surf", group: "waves", icon: "surf", unit: "spots",
    trackerKey: "surf", boardPath: "/conditions/surf", spotLens: null, awardSport: "surf",
    awardIds: { headline: "surf-spot-of-the-year", secondary: "surf-most-epic-days" },
    beta: false, seasonal: false, keywords: "surf surfing waves swell beach",
  },
  {
    key: "bodyboarding", label: "Bodyboarding", lowerName: "bodyboarding", group: "waves", icon: "bodyboarding", unit: "spots",
    trackerKey: "bodyboarding", boardPath: "/conditions/bodyboarding", spotLens: "bodyboarding", awardSport: "bodyboarding",
    awardIds: { headline: "bodyboard-spot-of-the-year", secondary: "bodyboard-shorebreak-slammer" },
    beta: false, seasonal: false, keywords: "bodyboard bodyboarding boogie board shorebreak",
  },
  {
    key: "foil", label: "Prone foil", lowerName: "prone foiling", group: "waves", icon: "foil", unit: "spots",
    trackerKey: "foil", boardPath: "/conditions/foil", spotLens: "foil", awardSport: "foil",
    awardIds: { headline: "foil-spot-of-the-year", secondary: "foil-glassiest" },
    beta: false, seasonal: false, keywords: "prone foil foiling hydrofoil surf foil",
  },
  {
    key: "sup-surf", label: "SUP surf", lowerName: "SUP surf", group: "waves", icon: "sup", unit: "spots",
    trackerKey: "sup-surf", boardPath: "/conditions/sup-surf", spotLens: "sup-surf", awardSport: "sup-surf",
    awardIds: { headline: "sup-surf-day", secondary: null },
    beta: true, seasonal: false, keywords: "sup surf paddleboard paddle board stand up paddle waves",
  },
  {
    key: "sup", label: "SUP", lowerName: "SUP", group: "waves", icon: "sup", unit: "spots",
    trackerKey: "sup", boardPath: "/conditions/sup", spotLens: "sup", awardSport: "sup",
    awardIds: { headline: "sup-flatwater-paradise", secondary: null },
    beta: false, seasonal: false, keywords: "sup paddleboard paddle board stand up paddle",
  },
  {
    key: "wind", label: "Kite and windsurf", lowerName: "kite and windsurf", group: "wind", icon: "wind", unit: "spots",
    trackerKey: "wind", boardPath: "/conditions/wind", spotLens: "wind", awardSport: "wind",
    awardIds: { headline: "wind-capital", secondary: "wind-epic-days" },
    beta: false, seasonal: false, keywords: "kite kitesurf kitesurfing kiteboarding windsurf windsurfing wind",
  },
  {
    key: "wing-foil", label: "Wing foil", lowerName: "wing foiling", group: "wind", icon: "wing-foil", unit: "spots",
    trackerKey: "wing-foil", boardPath: "/conditions/wing-foil", spotLens: "wing-foil", awardSport: "wing-foil",
    awardIds: { headline: "wing-foil-spot-of-the-year", secondary: "wing-powered-up" },
    beta: false, seasonal: false, keywords: "wing foil wingfoil wingfoiling winging wing",
  },
  {
    key: "downwind-foil", label: "Downwind foil", lowerName: "downwind foiling", group: "wind", icon: "downwind-foil", unit: "spots",
    trackerKey: "downwind-foil", boardPath: "/conditions/downwind-foil", spotLens: "downwind-foil", awardSport: "downwind-foil",
    awardIds: { headline: "downwind-foil-spot-of-the-year", secondary: "downwind-bump-highway" },
    beta: false, seasonal: false, keywords: "downwind foil downwinder downwinding bumps",
  },
  {
    key: "sup-downwind", label: "Downwind SUP", lowerName: "downwind SUP", group: "wind", icon: "sup", unit: "spots",
    trackerKey: "sup-downwind", boardPath: "/conditions/sup-downwind", spotLens: "sup-downwind", awardSport: "sup-downwind",
    awardIds: { headline: "sup-downwind-run", secondary: null },
    beta: true, seasonal: false, keywords: "downwind sup downwinder paddleboard paddle board stand up paddle",
  },
  {
    key: "sailing", label: "Sailing", lowerName: "sailing", group: "wind", icon: "sailing", unit: "venues",
    trackerKey: "sailing-club", boardPath: "/conditions/sailing", spotLens: "sailing-club", awardSport: "sailing",
    awardIds: { headline: "sailing-capital", secondary: null },
    beta: false, seasonal: false, keywords: "sailing sail dinghy yacht regatta club",
  },
  {
    key: "diving", label: "Diving", lowerName: "diving", group: "water", icon: "diving", unit: "spots",
    trackerKey: "diving", boardPath: "/conditions/diving", spotLens: "diving", awardSport: "diving",
    awardIds: { headline: "diving-clearest-waters", secondary: "diving-vis-capital" },
    beta: false, seasonal: false, keywords: "diving dive scuba snorkel snorkelling freediving visibility",
  },
  {
    key: "open-water", label: "Open water", lowerName: "open-water swimming", group: "water", icon: "open-water", unit: "spots",
    trackerKey: "open-water", boardPath: "/conditions/open-water", spotLens: "swimming", awardSport: "swim",
    awardIds: { headline: "swim-spot-of-the-year", secondary: "swim-calmest" },
    beta: false, seasonal: false, keywords: "open water swim swimming wild swimming sea swim",
  },
  {
    key: "sup-touring", label: "Touring SUP", lowerName: "touring SUP", group: "water", icon: "sup", unit: "spots",
    trackerKey: "sup-touring", boardPath: "/conditions/sup-touring", spotLens: "sup-touring", awardSport: "sup-touring",
    awardIds: { headline: "sup-touring-glass-day", secondary: null },
    beta: true, seasonal: false, keywords: "touring sup paddleboard paddle board stand up paddle flatwater",
  },
] satisfies HubSport[]);

const BY_KEY: ReadonlyMap<string, HubSport> = new Map(HUB_SPORTS.map((sport) => [sport.key, sport]));

/** The named paddle profile keys, shown only when they are public. */
export const HUB_BETA_SPORT_KEYS: readonly HubSportKey[] = Object.freeze(["sup-surf", "sup-downwind", "sup-touring"]);
const LEGACY_PADDLE_KEY: HubSportKey = "sup";

export function isHubSportKey(value: string): value is HubSportKey {
  return BY_KEY.has(value);
}

export function hubSport(key: HubSportKey): HubSport {
  const sport = BY_KEY.get(key);
  if (!sport) throw new Error(`unknown hub sport ${key}`);
  return sport;
}

/**
 * The sports the hub shows, picker order: 16 with the named paddle profiles public, else 14 with
 * the legacy board in Waves.
 */
export function hubSportsFor(namedPublic: boolean): readonly HubSport[] {
  return HUB_SPORTS.filter((sport) =>
    namedPublic ? sport.key !== LEGACY_PADDLE_KEY : !HUB_BETA_SPORT_KEYS.includes(sport.key));
}

export function hubSportsInGroup(sports: readonly HubSport[], group: HubSportGroup): readonly HubSport[] {
  return sports.filter((sport) => sport.group === group);
}

/**
 * A shareable `?sport=` value as a shown hub sport key, or null for All or an unknown value. The
 * legacy key opens Touring SUP when the named profiles are public, and a named key opens the
 * legacy board when they are not.
 */
export function normaliseHubSportParam(value: string | null | undefined, namedPublic: boolean): HubSportKey | null {
  if (!value || value === "all" || !isHubSportKey(value)) return null;
  if (namedPublic && value === LEGACY_PADDLE_KEY) return "sup-touring";
  if (!namedPublic && HUB_BETA_SPORT_KEYS.includes(value)) return LEGACY_PADDLE_KEY;
  return value;
}
